import { useState, useEffect, useRef, forwardRef } from "react";
import { CaretDownIcon, MagnifyingGlassIcon, XCircleIcon } from "@phosphor-icons/react";

export interface IDropdownItem {
  content: string;
  value: string | number | boolean;
  disabled?: boolean;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  active?: boolean;
}

interface IDropdownItemProps extends IDropdownItem {
  onClick: (args: any) => void;
}

export const DropdownItem = ({ prefix, content, suffix, active, onClick, disabled }: Partial<IDropdownItemProps>) => {
  return (
    <li onClick={onClick} className={`${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}>
      {prefix && <div className="prefix">{prefix}</div>}
      <div>
        <div className="content">{content}</div>
        {suffix && <div className="suffix">{suffix}</div>}
      </div>
    </li>
  );
};

export interface IDropdownProps {
  dataSource: IDropdownItem[];
  placeholder?: string;
  searchable?: boolean;
  onSearch?: (value: string) => void;
  onChange?: (value: any) => void;
  value?: string;
  disabled?: boolean;
  closeEnable?: boolean
}

const Dropdown = forwardRef<HTMLDivElement, IDropdownProps>(
  ({ dataSource, placeholder = "Select", searchable, onSearch, onChange, value, disabled, closeEnable }, ref) => {
    const [visible, setVisible] = useState(false);
    const [select, setSelection] = useState<Pick<IDropdownItem, "content" | "value">>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchValue, setSearchValue] = useState('');
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      setSelection(dataSource.find(item => item.value === value) || { content: '', value: '' });
    }, [value]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef && dropdownRef.current && !dropdownRef.current?.contains(event.target as Node)) {
          setVisible(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [dropdownRef]);

    useEffect(() => {
      setSearchValue('');
    }, [visible]);

    useEffect(() => {
      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, []);

    const changeHandler = (changeValue: Pick<IDropdownItem, "content" | "value">) => {
      setSelection(changeValue);
      setVisible(false);
      if (onChange) {
        onChange(changeValue.value);
      }
    };

    const reset = (e: any) => {
      if (!disabled) {
        e.stopPropagation();
        changeHandler({ content: '', value: '' });
      }
    }

    // helper to merge internal ref with forwarded ref
    const setRefs = (node: HTMLDivElement | null) => {
      // set internal ref
      (dropdownRef as any).current = node;
      // forward to parent ref (supports callback and object refs)
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as any).current = node;
      }
    };

    return (
      <div
        ref={setRefs}
        className={`dropdown-container ${visible ? 'focusing' : ''} ${disabled ? 'dropdown-container__disabled' : ''}`}
        onClick={() => !disabled && setVisible(!visible)}
      >
        <div>
          <span className={`place-holder-dropdown ${(placeholder && !select?.value) ? 'placeholder' : ''}`} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {select?.content || placeholder}
          </span>
          {select?.value && closeEnable ? (
            <XCircleIcon className="clear-button" size={20} onClick={reset} />
          ) : (
            <CaretDownIcon size={20} />
          )}
        </div>
        {visible && (
          <div className="dropdown-popup">
            {searchable && (
              <div className="search-box" onClick={(e) => e.stopPropagation()}>
                <MagnifyingGlassIcon />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setSearchValue(value);

                    // Debounce onSearch to prevent dropdown from closing while typing
                    if (onSearch) {
                      if (debounceRef.current) {
                        clearTimeout(debounceRef.current);
                      }
                      debounceRef.current = setTimeout(() => {
                        onSearch(value);
                      }, 500);
                    }
                  }}
                />
              </div>
            )}
            <ul>
              {dataSource.filter(({ content }) => content.toLowerCase().includes(searchValue.toLowerCase())).map(({ prefix, content, suffix, value, disabled }, index) => (
                <DropdownItem
                  key={index}
                  prefix={prefix}
                  content={content}
                  suffix={suffix}
                  active={value === select?.value}
                  onClick={() => !disabled && changeHandler({ value, content })}
                  disabled={disabled}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
